import React, { useEffect, useState } from "react";
import './../css/blog.scss';
import { useParams, Navigate, Link } from 'react-router-dom';
import { BsCalendar4Week, BsClock, BsArrowLeft } from 'react-icons/bs';
import SEO from './../components/SEO/SEO';
import { generateArticleSchema } from './../helpers/generateFAQSchema';
import BlogData from './../data/blog.json';
import useAnalyticsScrollEvent from './../helpers/useAnalyticsScrollEvent';

function BlogArticle(props) {
  useAnalyticsScrollEvent('Blog Article');

  // Get slug from URL parameter
  const { slug } = useParams();

  // Find article by slug
  const article = BlogData.blog.list.find(item => item.slug === slug);

  // If article not found, redirect to blog
  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const {
    categories = [],
    headline,
    description,
    excerpt,
    content,
    authorImage,
    author = "Wissile Sogoyou",
    authorRole = "Meetworkplace Team",
    date,
    datePublished,
    dateModified,
    time,
    image,
    keywords = []
  } = article;

  // Generate Article Schema for SEO
  const imageUrl = image.startsWith('http') ? image : `https://tomeetworkplace.com/assets/${image}.jpg`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": headline,
    "description": excerpt || description,
    "image": imageUrl,
    "datePublished": datePublished,
    "dateModified": dateModified || new Date().toISOString(),
    "author": {
      "@type": "Organization",
      "name": author,
      "url": "https://tomeetworkplace.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Meetworkplace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://tomeetworkplace.com/logo512.png",
        "width": 512,
        "height": 512
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://tomeetworkplace.com/blog/${slug}`
    },
    "keywords": keywords.join(', '),
    "articleSection": "Employee Retention",
    "wordCount": content ? content.split(' ').length : 500
  };

  // Generate SEO title and description
  const seoTitle = `${headline} | Meetworkplace Blog`;
  const seoDescription = excerpt || description;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={keywords}
        type="article"
        structuredData={articleSchema}
        canonical={`https://tomeetworkplace.com/blog/${slug}`}
        image={imageUrl}
      />

      <section className="white blog-article">
        <div className="header">
          <div className="back-to-blog">
            <div className="container">
              <Link to="/blog" className="back-link">
                <BsArrowLeft size={20} />
                <span>Back to Blog</span>
              </Link>
            </div>
          </div>
          <div className="container row">
            <div className="col-xs-12 col-sm-6">
              <div className="pill-container">
                {categories.map((category, idx) => (
                  <span key={idx} className="pill">{category}</span>
                ))}
              </div>
              <div className="title-container">
                <h1>{headline}</h1>
              </div>
              <div className="auth-container">
                <div className="author-info">
                  {authorImage && (
                    <div className="author-image">
                      <img src={require(`./../assets/${authorImage}`)} alt={author} />
                    </div>
                  )}
                  <div className="author-text">
                    <div className="author-name">{author}</div>
                    <div className="author-role">{authorRole}</div>
                  </div>
                </div>
              </div>
              <div className="author-meta">
                <span className="author-date">
                  <BsCalendar4Week size={18}/> {date}
                </span>
                <span className="author-time">
                  <BsClock size={18}/> {time}
                </span>
              </div>
            </div>

            <div className="col-xs-12 col-sm-6">
              <div className="main-image">
                <img
                  src={image.startsWith('http') ? image : require(`./../assets/${image}.jpg`)}
                  alt={headline}
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="container row">
          <div className="col-xs-12 col-sm-8">
            <div className="desc-container">
              <article dangerouslySetInnerHTML={{__html:content}}></article>
            </div>
          </div>

          {/* Sidebar for related articles */}
          <div className="col-xs-12 col-sm-4">
            <div className="sidebar-container">
              <h3 className="sidebar-title">Related Articles</h3>
              <div className="related-articles">
                {BlogData.blog.list
                  .filter(item => item.slug !== slug)
                  .slice(0, 3)
                  .map((item, i) => (
                    <div key={i} className="related-article">
                      <Link to={`/blog/${item.slug}`}>
                        <h4 className="related-title">{item.headline}</h4>
                        <p className="related-excerpt">{item.excerpt || item.description.substring(0, 100)}...</p>
                      </Link>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogArticle;
